import {
  computed,
  inject,
  reactive,
  type ComputedRef,
  type InjectionKey
} from 'vue'
import {
  addCabinetConfigurationToDraft,
  addEntityToDraft,
  APP_VERSION,
  createSolutionDraft,
  isVersionAtLeast,
  moveDraftItem,
  removeEntityFromDraft,
  updateDraftLabels,
  type CabinetConfigurationSnapshot,
  type CompanyProfile,
  type ContentEntity,
  type ContentManifest,
  type MediaAsset,
  type Product,
  type SolutionDraft
} from '@bf/domain'
import { findAsset, findEntity, validateContent } from '@bf/content-schema'
import { runtimeConfig } from '../platform/runtime'

export interface SessionUser {
  userId: string
  displayName: string
  role: 'sales' | 'technician'
  mock: boolean
}

interface ShowroomState {
  loading: boolean
  error: string
  manifest: ContentManifest | null
  session: SessionUser | null
  draft: SolutionDraft | null
  notice: string
}

export interface ShowroomStore {
  state: ShowroomState
  entities: ComputedRef<ContentEntity[]>
  company: ComputedRef<CompanyProfile | null>
  load: (silent?: boolean) => Promise<void>
  getEntity: (id: string) => ContentEntity | undefined
  getAsset: (id: string) => MediaAsset | undefined
  getCabinetLayoutAsset: (layoutTemplateId: string) => MediaAsset | undefined
  mediaUrl: (asset: MediaAsset) => string
  addToDraft: (entity: ContentEntity) => void
  addCabinetConfiguration: (configuration: CabinetConfigurationSnapshot) => void
  removeFromDraft: (itemId: string) => void
  moveInDraft: (fromIndex: number, toIndex: number) => void
  setDraftLabels: (customerLabel: string, projectLabel: string) => void
  clearDraft: () => void
  dismissNotice: () => void
}

const sessionDraftKey = 'bf-showroom-draft-v0.5.2'
const legacySessionDraftKey = 'bf-showroom-draft-v0.2.8'

function getDraftStorage(kind: 'local' | 'session'): Storage | null {
  try {
    return kind === 'local' ? globalThis.localStorage : globalThis.sessionStorage
  } catch {
    return null
  }
}

function removeStoredValue(storage: Storage | null, key: string) {
  if (storage === null) return
  try {
    storage.removeItem(key)
  } catch {
    // Storage can be blocked by the host; the in-memory draft remains usable.
  }
}

function writeStoredValue(
  storage: Storage | null,
  key: string,
  value: string
): boolean {
  if (storage === null) return false
  try {
    storage.setItem(key, value)
    return true
  } catch {
    return false
  }
}

function isStoredDraft(value: unknown): value is SolutionDraft {
  if (typeof value !== 'object' || value === null) return false
  const draft = value as Partial<SolutionDraft>
  return (
    typeof draft.contentVersion === 'string' &&
    typeof draft.customerLabel === 'string' &&
    typeof draft.projectLabel === 'string' &&
    typeof draft.createdAt === 'string' &&
    Array.isArray(draft.items) &&
    draft.items.every(
      (item) =>
        typeof item === 'object' &&
        item !== null &&
        typeof item.itemId === 'string' &&
        typeof item.entityId === 'string' &&
        typeof item.contentType === 'string' &&
        typeof item.title === 'string'
    )
  )
}

function readDraftFromStorage(
  storage: Storage | null,
  key: string
): SolutionDraft | null {
  if (storage === null) return null
  try {
    const value = storage.getItem(key)
    if (value === null) return null
    const parsed: unknown = JSON.parse(value)
    if (isStoredDraft(parsed)) return parsed
    removeStoredValue(storage, key)
  } catch {
    removeStoredValue(storage, key)
  }
  return null
}

function readStoredDraft(): SolutionDraft | null {
  const localDraftStorage = getDraftStorage('local')
  const storedDraft = readDraftFromStorage(localDraftStorage, sessionDraftKey)
  if (storedDraft !== null) return storedDraft

  const sessionDraftStorage = getDraftStorage('session')
  const legacyDraft =
    readDraftFromStorage(localDraftStorage, legacySessionDraftKey) ??
    readDraftFromStorage(sessionDraftStorage, legacySessionDraftKey)
  if (legacyDraft === null) return null

  if (
    writeStoredValue(
      localDraftStorage,
      sessionDraftKey,
      JSON.stringify(legacyDraft)
    )
  ) {
    removeStoredValue(localDraftStorage, legacySessionDraftKey)
    removeStoredValue(sessionDraftStorage, legacySessionDraftKey)
  }
  return legacyDraft
}

function saveDraft(draft: SolutionDraft | null) {
  const localDraftStorage = getDraftStorage('local')
  if (draft === null) {
    removeStoredValue(localDraftStorage, sessionDraftKey)
    removeStoredValue(localDraftStorage, legacySessionDraftKey)
    removeStoredValue(getDraftStorage('session'), legacySessionDraftKey)
    return
  }

  if (
    writeStoredValue(localDraftStorage, sessionDraftKey, JSON.stringify(draft))
  ) {
    removeStoredValue(localDraftStorage, legacySessionDraftKey)
    removeStoredValue(getDraftStorage('session'), legacySessionDraftKey)
  }
}

async function readSession(): Promise<SessionUser> {
  if (!runtimeConfig.requiresSession) {
    return {
      userId: 'desktop-local-user',
      displayName: '本机演示人员',
      role: 'technician',
      mock: false
    }
  }

  const response = await fetch('/api/auth/session', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' }
  })
  if (response.status === 401) {
    const next = `${window.location.pathname}${window.location.hash}`
    window.location.assign(`/wecom/entry?next=${encodeURIComponent(next)}`)
    throw new Error('正在进入企业微信身份验证')
  }
  if (!response.ok) throw new Error('无法验证企业微信身份')
  return (await response.json()) as SessionUser
}

export function createShowroomStore(): ShowroomStore {
  const state = reactive<ShowroomState>({
    loading: true,
    error: '',
    manifest: null,
    session: null,
    draft: readStoredDraft(),
    notice: ''
  })

  const entities = computed(() => state.manifest?.entities ?? [])
  const company = computed(
    () =>
      (entities.value.find(
        (entity) => entity.contentType === 'company'
      ) as CompanyProfile | undefined) ?? null
  )

  async function load(silent = false) {
    if (!silent) state.loading = true
    state.error = ''
    try {
      state.session = await readSession()
      const contentUrl = new URL(runtimeConfig.contentUrl, window.location.href)
      contentUrl.searchParams.set('_refresh', Date.now().toString())
      const response = await fetch(contentUrl, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'no-cache'
        }
      })
      if (!response.ok) throw new Error(`内容加载失败（${response.status}）`)
      const result = validateContent((await response.json()) as unknown)
      if (!result.success || result.data === undefined) {
        const first = result.issues[0]
        throw new Error(
          first === undefined
            ? '内容校验失败'
            : `内容校验失败：${first.path} ${first.message}`
        )
      }
      if (!isVersionAtLeast(APP_VERSION, result.data.minAppVersion)) {
        throw new Error('当前程序版本低于内容要求版本')
      }
      state.manifest = result.data
      if (state.draft === null || state.draft.contentVersion !== result.data.contentVersion) {
        state.draft = createSolutionDraft(result.data.contentVersion)
        saveDraft(state.draft)
        state.notice = '内容版本已更新，已创建新的演示方案'
      }
    } catch (error) {
      if (!silent) state.error = error instanceof Error ? error.message : '加载失败'
    } finally {
      if (!silent) state.loading = false
    }
  }

  function getEntity(id: string) {
    return state.manifest === null ? undefined : findEntity(state.manifest, id)
  }

  function getAsset(id: string) {
    return state.manifest === null ? undefined : findAsset(state.manifest, id)
  }

  function getCabinetLayoutAsset(layoutTemplateId: string) {
    const candidates =
      state.manifest?.assets.filter(
        (asset) =>
          asset.mediaType === 'image' &&
          asset.displayRole === 'cabinet-layout' &&
          asset.layoutTemplateId === layoutTemplateId
      ) ?? []
    return candidates.find((asset) => asset.preferred) ?? candidates[0]
  }

  function mediaUrl(asset: MediaAsset) {
    if (runtimeConfig.mode === 'wecom') {
      return `/api/media/${encodeURIComponent(asset.id)}`
    }
    const safeSegments = asset.path.split('/').map(encodeURIComponent)
    return `/content/media/${safeSegments.join('/')}`
  }

  function requireDraft(): SolutionDraft {
    if (state.draft !== null) return state.draft
    if (state.manifest === null) throw new Error('内容尚未加载')
    state.draft = createSolutionDraft(state.manifest.contentVersion)
    return state.draft
  }

  function addToDraft(entity: ContentEntity) {
    try {
      const draft = requireDraft()
      const alreadyIncluded = draft.items.some(
        (item) => item.itemId === entity.id
      )
      const selectedStyle = draft.items.find(
        (item) => item.contentType === 'software-style'
      )
      state.draft = addEntityToDraft(draft, entity)
      saveDraft(state.draft)
      if (entity.contentType === 'software-style') {
        state.notice =
          selectedStyle?.entityId === entity.id
            ? `当前已选方案风格：${entity.title}`
            : selectedStyle === undefined
              ? `已选定方案风格：${entity.title}`
              : `已切换方案风格：${entity.title}`
      } else {
        state.notice = alreadyIncluded
          ? `方案中已有：${entity.title}`
          : `已加入方案：${entity.title}`
      }
    } catch (error) {
      state.notice = error instanceof Error ? error.message : '无法加入方案'
    }
  }

  function addCabinetConfiguration(
    configuration: CabinetConfigurationSnapshot
  ) {
    try {
      const entity = getEntity(configuration.cabinetProfileId)
      if (
        entity === undefined ||
        entity.contentType !== 'product' ||
        entity.productCategory !== 'cabinet'
      ) {
        throw new Error('推荐机柜内容不存在或已撤回')
      }
      const draft = requireDraft()
      const alreadyIncluded = draft.items.some(
        (item) =>
          item.configuration?.kind === 'cabinet-layout' &&
          item.configuration.cabinetProfileId ===
            configuration.cabinetProfileId &&
          item.configuration.systemId === configuration.systemId &&
          item.configuration.pointCount === configuration.pointCount
      )
      state.draft = addCabinetConfigurationToDraft(
        draft,
        entity as Product,
        configuration
      )
      saveDraft(state.draft)
      state.notice = alreadyIncluded
        ? `方案中已有：${configuration.cabinetTitle} / ${configuration.systemLabel}`
        : `已加入方案：${configuration.cabinetTitle} / ${configuration.systemLabel}`
    } catch (error) {
      state.notice = error instanceof Error ? error.message : '无法加入机柜配置'
    }
  }

  function removeFromDraft(itemId: string) {
    state.draft = removeEntityFromDraft(requireDraft(), itemId)
    saveDraft(state.draft)
  }

  function moveInDraft(fromIndex: number, toIndex: number) {
    state.draft = moveDraftItem(requireDraft(), fromIndex, toIndex)
    saveDraft(state.draft)
  }

  function setDraftLabels(customerLabel: string, projectLabel: string) {
    state.draft = updateDraftLabels(requireDraft(), {
      customerLabel,
      projectLabel
    })
    saveDraft(state.draft)
  }

  function clearDraft() {
    if (state.manifest === null) return
    state.draft = createSolutionDraft(state.manifest.contentVersion)
    saveDraft(state.draft)
    state.notice = '本次演示方案已清空'
  }

  function dismissNotice() {
    state.notice = ''
  }

  return {
    state,
    entities,
    company,
    load,
    getEntity,
    getAsset,
    getCabinetLayoutAsset,
    mediaUrl,
    addToDraft,
    addCabinetConfiguration,
    removeFromDraft,
    moveInDraft,
    setDraftLabels,
    clearDraft,
    dismissNotice
  }
}

export const showroomStoreKey: InjectionKey<ShowroomStore> = Symbol('showroom-store')

export function useShowroomStore(): ShowroomStore {
  const store = inject(showroomStoreKey)
  if (store === undefined) throw new Error('Showroom store is not available')
  return store
}
